import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Search, PackageSearch, AlertTriangle, PackageX, IndianRupee, History,
  Plus, Warehouse, ArrowLeftRight, Download, CalendarClock, Trash2,
} from "lucide-react";
import { api, apiError } from "@/lib/api";
import { inr, num, fmtDate, titleCase, today } from "@/lib/format";
import { useCanEdit } from "@/context/AuthContext";
import { CardList, DataCard, TableWrap } from "@/components/DataCards";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

const STATUS_TONE = {
  OK: "bg-emerald-50 text-emerald-700",
  LOW: "bg-amber-50 text-amber-700",
  OUT_OF_STOCK: "bg-rose-50 text-rose-700",
};
const STATUS_LABEL = { OK: "In stock", LOW: "Low", OUT_OF_STOCK: "Out of stock" };

const ADJUST_TYPES = [
  ["ADJUSTMENT", "Adjustment"],
  ["WRITE_OFF", "Write-off / damage"],
  ["PURCHASE_RETURN", "Purchase return"],
];

const TABS = [
  ["stock", "Stock"],
  ["receipts", "Purchase receipts"],
  ["warehouses", "Warehouses"],
  ["reports", "Reports"],
];

const Tile = ({ icon: Icon, label, value, testid }) => (
  <div className="rounded-lg border border-slate-200 bg-white p-4" data-testid={testid}>
    <div className="flex items-center gap-2 text-[11px] uppercase tracking-wide text-slate-500">
      <Icon size={13} className="text-blue-500" />
      {label}
    </div>
    <div className="mt-2 font-heading text-2xl font-bold tabular-nums">{value}</div>
  </div>
);

export default function Inventory() {
  const canEdit = useCanEdit();
  const navigate = useNavigate();
  // Recording a receipt now leaves the page, so coming back has to be able to
  // land on the tab that shows the thing just recorded.
  const [params, setParams] = useSearchParams();
  const [tab, setTab] = useState(
    () => (TABS.some(([key]) => key === params.get("tab")) ? params.get("tab") : "stock"));

  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [warehouses, setWarehouses] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [q, setQ] = useState("");
  const [only, setOnly] = useState("ALL");
  const [whFilter, setWhFilter] = useState("ALL");

  const [adjusting, setAdjusting] = useState(null);
  const [adjustForm, setAdjustForm] = useState({});
  const [movementsFor, setMovementsFor] = useState(null);
  const [movements, setMovements] = useState([]);
  const [breakdown, setBreakdown] = useState([]);

  const [whOpen, setWhOpen] = useState(false);
  const [whEditing, setWhEditing] = useState(null);
  const [whForm, setWhForm] = useState({ name: "", code: "", address: "", is_default: false, is_active: true });

  const [transferOpen, setTransferOpen] = useState(false);
  const [transfer, setTransfer] = useState({});

  const [report, setReport] = useState("valuation");
  const [reportData, setReportData] = useState(null);

  const loadStock = useCallback(() => {
    api.get("/inventory/stock", {
      params: { q: q || undefined, only: only === "ALL" ? undefined : only,
                warehouse_id: whFilter === "ALL" ? undefined : whFilter },
    }).then(({ data }) => setRows(data));
    api.get("/inventory/summary").then(({ data }) => setSummary(data));
  }, [q, only, whFilter]);

  const loadWarehouses = useCallback(
    () => api.get("/inventory/warehouses").then(({ data }) => setWarehouses(data)), []);

  useEffect(() => { loadStock(); }, [loadStock]);
  useEffect(() => {
    loadWarehouses();
  }, [loadWarehouses]);

  useEffect(() => {
    if (tab === "receipts") api.get("/inventory/receipts").then(({ data }) => setReceipts(data));
  }, [tab]);

  useEffect(() => {
    if (tab !== "reports") return;
    const url = { valuation: "/inventory/reports/valuation",
                  expiry: "/inventory/reports/expiry",
                  reorder: "/inventory/reports/reorder",
                  cogs: "/inventory/reports/cogs" }[report];
    api.get(url).then(({ data }) => setReportData(data));
  }, [tab, report]);

  const openAdjust = (item) => {
    setAdjustForm({
      mode: "CHANGE", quantity: "", new_quantity: "", movement_type: "ADJUSTMENT",
      reason: "", warehouse_id: warehouses[0]?.id || "", batch_number: "",
    });
    setAdjusting(item);
  };

  const saveAdjust = async () => {
    const payload = {
      item_id: adjusting.id, movement_type: adjustForm.movement_type,
      reason: adjustForm.reason, warehouse_id: adjustForm.warehouse_id || undefined,
      batch_number: adjustForm.batch_number || undefined,
    };
    if (adjustForm.mode === "CHANGE") payload.quantity = Number(adjustForm.quantity);
    else payload.new_quantity = Number(adjustForm.new_quantity);
    try {
      await api.post("/inventory/adjust", payload);
      toast.success(`Stock updated for ${adjusting.name}`);
      setAdjusting(null);
      loadStock();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const openMovements = async (item) => {
    setMovementsFor(item);
    const [mv, bd] = await Promise.all([
      api.get("/inventory/movements", { params: { item_id: item.id } }),
      api.get(`/inventory/stock/${item.id}/breakdown`),
    ]);
    setMovements(mv.data);
    setBreakdown(bd.data);
  };

  const saveWarehouse = async () => {
    try {
      if (whEditing) await api.patch(`/inventory/warehouses/${whEditing.id}`, whForm);
      else await api.post("/inventory/warehouses", whForm);
      toast.success(whEditing ? "Warehouse updated" : "Warehouse created");
      setWhOpen(false);
      setWhEditing(null);
      loadWarehouses();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const removeWarehouse = async (w) => {
    if (!window.confirm(`Archive ${w.name}?`)) return;
    try {
      await api.delete(`/inventory/warehouses/${w.id}`);
      toast.success("Warehouse archived");
      loadWarehouses();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const openTransfer = () => {
    setTransfer({
      item_id: "", from_warehouse_id: warehouses[0]?.id || "",
      to_warehouse_id: warehouses[1]?.id || "", quantity: "", batch_number: "",
      notes: "", transfer_date: today(),
    });
    setTransferOpen(true);
  };

  const saveTransfer = async () => {
    try {
      const { data } = await api.post("/inventory/transfers", {
        ...transfer, quantity: Number(transfer.quantity),
        batch_number: transfer.batch_number || null,
      });
      toast.success(`Transferred · ${data.label}`);
      setTransferOpen(false);
      loadStock();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const exportCsv = async (path, filename) => {
    try {
      const { data } = await api.get(path, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not export. Please try again.");
    }
  };

  return (
    <div className="space-y-6" data-testid="inventory-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight">Inventory</h1>
          <p className="mt-1 text-sm text-slate-500">
            Warehouses, purchase receipts and moving-average valuation. Issuing an invoice reduces
            stock; a credit note puts it back.
          </p>
        </div>
        {canEdit && (
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" onClick={openTransfer} data-testid="new-transfer-btn"
              className="gap-1.5" disabled={warehouses.length < 2}>
              <ArrowLeftRight size={15} /> Transfer
            </Button>
            <Button size="sm" onClick={() => navigate("/inventory/receive")} data-testid="new-receipt-btn"
              className="gap-1.5 bg-blue-600 hover:bg-blue-700">
              <Plus size={15} /> Receive stock
            </Button>
          </div>
        )}
      </div>

      {summary && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
          <Tile icon={PackageSearch} label="Tracked items" value={summary.tracked_items} testid="inv-tile-tracked" />
          <Tile icon={AlertTriangle} label="Low stock" value={summary.low_stock} testid="inv-tile-low" />
          <Tile icon={PackageX} label="Out of stock" value={summary.out_of_stock} testid="inv-tile-out" />
          <Tile icon={CalendarClock} label="Expiring in 90d" value={summary.expiring_soon} testid="inv-tile-expiring" />
          <Tile icon={IndianRupee} label="Stock value" value={inr(summary.stock_value)} testid="inv-tile-value" />
        </div>
      )}

      <div className="flex flex-wrap gap-1 border-b border-slate-200">
        {TABS.map(([key, label]) => (
          <button key={key} data-testid={`inv-tab-${key}`}
            onClick={() => { setTab(key); setParams(key === "stock" ? {} : { tab: key }, { replace: true }); }}
            className={`-mb-px border-b-2 px-3 py-2 text-sm font-medium transition-colors duration-150 ${
              tab === key ? "border-blue-600 text-blue-700" : "border-transparent text-slate-500 hover:text-slate-800"
            }`}>
            {label}
          </button>
        ))}
      </div>

      {tab === "stock" && (
        <div className="space-y-4">
          {summary?.untracked_items > 0 && (
            <div className="rounded-md border border-slate-200 bg-slate-50 px-4 py-3 text-xs text-slate-600">
              {summary.untracked_items} item{summary.untracked_items === 1 ? " is" : "s are"} not tracked.
              Turn on <strong>Track inventory</strong> on an item, or just receive stock against it.
            </div>
          )}

          <div className="flex flex-wrap gap-3">
            <div className="relative w-full sm:w-64">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input data-testid="inventory-search" className="h-9 pl-8" value={q}
                onChange={(e) => setQ(e.target.value)} placeholder="Item name or code" />
            </div>
            <Select value={only} onValueChange={setOnly}>
              <SelectTrigger className="h-9 w-full sm:w-44" data-testid="inventory-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All tracked items</SelectItem>
                <SelectItem value="LOW">Low or out of stock</SelectItem>
                <SelectItem value="OUT">Out of stock only</SelectItem>
              </SelectContent>
            </Select>
            <Select value={whFilter} onValueChange={setWhFilter}>
              <SelectTrigger className="h-9 w-full sm:w-48" data-testid="inventory-warehouse-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All warehouses</SelectItem>
                {warehouses.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button size="sm" variant="outline" className="h-9 gap-1.5" data-testid="export-stock"
              onClick={() => exportCsv(
                `/inventory/export/stock.csv${whFilter === "ALL" ? "" : `?warehouse_id=${whFilter}`}`,
                "stock.csv")}>
              <Download size={14} /> CSV
            </Button>
          </div>

          <TableWrap>
            <table className="w-full text-sm" data-testid="inventory-table">
              <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-4 py-2.5 text-left font-semibold">Item</th>
                  <th className="px-3 py-2.5 text-left font-semibold">Code</th>
                  <th className="px-3 py-2.5 text-right font-semibold">In stock</th>
                  <th className="px-3 py-2.5 text-right font-semibold">Reorder at</th>
                  <th className="px-3 py-2.5 text-right font-semibold">Avg cost</th>
                  <th className="px-3 py-2.5 text-right font-semibold">Stock value</th>
                  <th className="px-3 py-2.5 text-center font-semibold">Status</th>
                  <th className="px-4 py-2.5 text-right font-semibold" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((r) => (
                  <tr key={r.id} className="hover:bg-slate-50 transition-colors duration-150" data-testid={`stock-row-${r.id}`}>
                    <td className="px-4 py-2.5 font-medium">
                      {r.name}
                      {r.track_batches && (
                        <span className="ml-2 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] uppercase text-slate-600">
                          Batch
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2.5 font-mono text-xs text-slate-600">{r.item_code || "—"}</td>
                    <td className="px-3 py-2.5 text-right font-mono tabular-nums">
                      <span className={r.stock_on_hand < 0 ? "text-rose-600 font-semibold" : ""}>
                        {num(r.stock_on_hand, 3)}
                      </span>
                      <span className="ml-1 text-[11px] text-slate-400">{r.unit_symbol}</span>
                    </td>
                    <td className="px-3 py-2.5 text-right font-mono text-xs text-slate-500">
                      {r.low_stock_threshold ? num(r.low_stock_threshold, 3) : "—"}
                    </td>
                    <td className="px-3 py-2.5 text-right font-mono text-xs">{inr(r.avg_cost)}</td>
                    <td className="px-3 py-2.5 text-right font-mono tabular-nums">{inr(r.stock_value)}</td>
                    <td className="px-3 py-2.5 text-center">
                      <span className={`rounded px-1.5 py-0.5 text-[11px] font-medium ${STATUS_TONE[r.status]}`}>
                        {STATUS_LABEL[r.status]}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      <div className="flex items-center justify-end gap-3">
                        <button onClick={() => openMovements(r)} data-testid={`stock-history-${r.id}`}
                          className="text-slate-400 transition-colors duration-150 hover:text-slate-700" title="History">
                          <History size={15} />
                        </button>
                        {canEdit && (
                          <button onClick={() => openAdjust(r)} data-testid={`stock-adjust-${r.id}`}
                            className="text-xs font-medium text-blue-700 transition-colors duration-150 hover:text-blue-900">
                            Adjust
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {!rows.length && (
                  <tr><td colSpan={8} className="px-4 py-14 text-center text-sm text-slate-500">
                    No tracked items yet — receive stock or enable tracking on an item.
                  </td></tr>
                )}
              </tbody>
            </table>
          </TableWrap>

          <CardList empty="No tracked items yet" testid="inventory-cards">
            {rows.map((r) => (
              <DataCard
                key={r.id}
                testid={`stock-card-${r.id}`}
                title={r.name}
                subtitle={r.item_code || undefined}
                right={
                  <>
                    <div className={`font-mono text-sm font-bold tabular-nums ${r.stock_on_hand < 0 ? "text-rose-600" : ""}`}>
                      {num(r.stock_on_hand, 3)} <span className="text-[11px] text-slate-400">{r.unit_symbol}</span>
                    </div>
                    <span className={`mt-1 inline-block rounded px-1.5 py-0.5 text-[10px] font-medium ${STATUS_TONE[r.status]}`}>
                      {STATUS_LABEL[r.status]}
                    </span>
                  </>
                }
                rows={[
                  ["Avg cost", inr(r.avg_cost)],
                  ["Stock value", inr(r.stock_value)],
                  r.low_stock_threshold ? ["Reorder at", num(r.low_stock_threshold, 3)] : null,
                ]}
                actions={
                  <>
                    <button className="text-xs font-medium text-slate-600"
                      data-testid={`stock-history-m-${r.id}`} onClick={() => openMovements(r)}>History</button>
                    {canEdit && (
                      <button className="text-xs font-medium text-blue-700"
                        data-testid={`stock-adjust-m-${r.id}`} onClick={() => openAdjust(r)}>Adjust</button>
                    )}
                  </>
                }
              />
            ))}
          </CardList>
        </div>
      )}

      {tab === "receipts" && (
        <div className="space-y-3">
          {receipts.map((r) => (
            <div key={r.id} className="rounded-md border border-slate-200 bg-white p-4" data-testid={`receipt-${r.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <span className="font-mono text-sm font-semibold">{r.receipt_number}</span>
                  <span className="ml-2 text-xs text-slate-500">{fmtDate(r.receipt_date)}</span>
                  <div className="mt-0.5 text-[11px] text-slate-500">
                    {r.warehouse_name}
                    {r.party_name ? ` · ${r.party_name}` : ""}
                    {r.reference_number ? ` · ref ${r.reference_number}` : ""}
                  </div>
                </div>
                <div className="font-mono text-sm font-bold tabular-nums">{inr(r.total_value)}</div>
              </div>
              <div className="mt-2 space-y-1 border-t border-slate-100 pt-2">
                {r.lines.map((l, i) => (
                  <div key={i} className="flex justify-between gap-3 text-xs">
                    <span className="min-w-0 truncate">
                      {l.item_name}
                      {l.batch_number ? ` · batch ${l.batch_number}` : ""}
                      {l.expiry_date ? ` · exp ${fmtDate(l.expiry_date)}` : ""}
                    </span>
                    <span className="shrink-0 font-mono text-slate-600">
                      {num(l.quantity, 3)} {l.unit_symbol} × {inr(l.unit_cost)} = {inr(l.value)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}
          {!receipts.length && (
            <div className="rounded-md border border-dashed border-slate-300 bg-white py-16 text-center text-sm text-slate-500">
              No purchase receipts yet. Receiving stock raises quantity and re-averages cost.
            </div>
          )}
        </div>
      )}

      {tab === "warehouses" && (
        <div className="space-y-4">
          {canEdit && (
            <Button size="sm" variant="outline" className="gap-1.5" data-testid="new-warehouse-btn"
              onClick={() => {
                setWhEditing(null);
                setWhForm({ name: "", code: "", address: "", is_default: false, is_active: true });
                setWhOpen(true);
              }}>
              <Plus size={15} /> New warehouse
            </Button>
          )}
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {warehouses.map((w) => (
              <div key={w.id} className="rounded-lg border border-slate-200 bg-white p-4" data-testid={`warehouse-${w.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <Warehouse size={14} className="text-slate-400" />
                      <span className="font-semibold">{w.name}</span>
                      {w.is_default && (
                        <span className="rounded bg-blue-50 px-1.5 py-0.5 text-[10px] uppercase text-blue-700">
                          Default
                        </span>
                      )}
                    </div>
                    {w.code && <div className="mt-0.5 font-mono text-[11px] text-slate-500">{w.code}</div>}
                    {w.address && <div className="mt-1 text-[11px] text-slate-500">{w.address}</div>}
                  </div>
                  {canEdit && (
                    <button onClick={() => removeWarehouse(w)} data-testid={`warehouse-delete-${w.id}`}
                      className="text-slate-400 transition-colors duration-150 hover:text-rose-600">
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
                <div className="mt-3 flex items-center justify-between border-t border-slate-100 pt-3 text-[11px] text-slate-500">
                  <span>{w.item_count} item(s) · {num(w.total_quantity, 3)} units</span>
                  {canEdit && (
                    <button className="font-medium text-blue-700" data-testid={`warehouse-edit-${w.id}`}
                      onClick={() => { setWhEditing(w); setWhForm({ ...w }); setWhOpen(true); }}>
                      Edit
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "reports" && (
        <div className="space-y-4">
          <div className="flex flex-wrap gap-2">
            {[["valuation", "Valuation"], ["reorder", "Reorder"],
              ["expiry", "Expiry"], ["cogs", "COGS"]].map(([k, label]) => (
              <button key={k} data-testid={`report-${k}`} onClick={() => setReport(k)}
                className={`inline-flex min-h-[36px] items-center rounded-full border px-3 py-1 text-[11px] font-semibold uppercase tracking-wide transition-colors duration-150 sm:min-h-0 ${
                  report === k ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-white text-slate-600 hover:border-slate-400"
                }`}>
                {label}
              </button>
            ))}
          </div>

          {report === "valuation" && reportData && (
            <div className="overflow-x-auto rounded-md border border-slate-200 bg-white">
              <table className="w-full text-sm" data-testid="valuation-table">
                <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-4 py-2.5 text-left font-semibold">Item</th>
                    <th className="px-3 py-2.5 text-right font-semibold">Qty</th>
                    <th className="px-3 py-2.5 text-right font-semibold">Avg cost</th>
                    <th className="px-4 py-2.5 text-right font-semibold">Value</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {reportData.rows?.map((r) => (
                    <tr key={r.id}>
                      <td className="px-4 py-2.5">{r.name}</td>
                      <td className="px-3 py-2.5 text-right font-mono">{num(r.stock_on_hand, 3)} {r.unit_symbol}</td>
                      <td className="px-3 py-2.5 text-right font-mono text-xs">{inr(r.avg_cost)}</td>
                      <td className="px-4 py-2.5 text-right font-mono font-semibold">{inr(r.stock_value)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-slate-900 text-white">
                    <td className="px-4 py-2.5 text-xs uppercase tracking-widest font-semibold" colSpan={3}>
                      Total stock value
                    </td>
                    <td className="px-4 py-2.5 text-right font-mono font-bold" data-testid="valuation-total">
                      {inr(reportData.total_value)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}

          {report === "reorder" && (
            <div className="overflow-x-auto rounded-md border border-slate-200 bg-white">
              <table className="w-full text-sm" data-testid="reorder-table">
                <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-4 py-2.5 text-left font-semibold">Item</th>
                    <th className="px-3 py-2.5 text-right font-semibold">In stock</th>
                    <th className="px-3 py-2.5 text-right font-semibold">Reorder at</th>
                    <th className="px-4 py-2.5 text-right font-semibold">Suggested order</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {Array.isArray(reportData) && reportData.map((r) => (
                    <tr key={r.id}>
                      <td className="px-4 py-2.5">{r.name}</td>
                      <td className="px-3 py-2.5 text-right font-mono">{num(r.stock_on_hand, 3)}</td>
                      <td className="px-3 py-2.5 text-right font-mono text-slate-500">{num(r.low_stock_threshold, 3)}</td>
                      <td className="px-4 py-2.5 text-right font-mono font-semibold">{num(r.suggested_order, 3)}</td>
                    </tr>
                  ))}
                  {Array.isArray(reportData) && !reportData.length && (
                    <tr><td colSpan={4} className="px-4 py-12 text-center text-sm text-slate-500">
                      Nothing needs reordering
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {report === "expiry" && (
            <div className="overflow-x-auto rounded-md border border-slate-200 bg-white">
              <table className="w-full text-sm" data-testid="expiry-table">
                <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-4 py-2.5 text-left font-semibold">Item</th>
                    <th className="px-3 py-2.5 text-left font-semibold">Batch</th>
                    <th className="px-3 py-2.5 text-left font-semibold">Warehouse</th>
                    <th className="px-3 py-2.5 text-left font-semibold">Expiry</th>
                    <th className="px-4 py-2.5 text-right font-semibold">Qty</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {Array.isArray(reportData) && reportData.map((r, i) => (
                    <tr key={i}>
                      <td className="px-4 py-2.5">{r.item_name}</td>
                      <td className="px-3 py-2.5 font-mono text-xs">{r.batch_number || "—"}</td>
                      <td className="px-3 py-2.5 text-xs text-slate-600">{r.warehouse_name}</td>
                      <td className="px-3 py-2.5">
                        <span className={`rounded px-1.5 py-0.5 text-[11px] font-medium ${
                          r.status === "EXPIRED" ? "bg-rose-50 text-rose-700" : "bg-amber-50 text-amber-700"}`}>
                          {fmtDate(r.expiry_date)}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-right font-mono">{num(r.quantity, 3)} {r.unit_symbol}</td>
                    </tr>
                  ))}
                  {Array.isArray(reportData) && !reportData.length && (
                    <tr><td colSpan={5} className="px-4 py-12 text-center text-sm text-slate-500">
                      Nothing expiring in the next 90 days
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {report === "cogs" && reportData && (
            <div className="overflow-x-auto rounded-md border border-slate-200 bg-white">
              <div className="border-b border-slate-100 px-4 py-2.5 text-xs text-slate-500">
                {fmtDate(reportData.from_date)} — {fmtDate(reportData.to_date)}
              </div>
              <table className="w-full text-sm" data-testid="cogs-table">
                <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-4 py-2.5 text-left font-semibold">Item</th>
                    <th className="px-3 py-2.5 text-right font-semibold">Qty sold</th>
                    <th className="px-4 py-2.5 text-right font-semibold">Cost of goods sold</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {reportData.rows?.map((r) => (
                    <tr key={r.item_id}>
                      <td className="px-4 py-2.5">{r.item_name}</td>
                      <td className="px-3 py-2.5 text-right font-mono">{num(r.quantity, 3)} {r.unit_symbol}</td>
                      <td className="px-4 py-2.5 text-right font-mono font-semibold">{inr(r.cogs)}</td>
                    </tr>
                  ))}
                  {!reportData.rows?.length && (
                    <tr><td colSpan={3} className="px-4 py-12 text-center text-sm text-slate-500">
                      No tracked sales in this period
                    </td></tr>
                  )}
                </tbody>
                <tfoot>
                  <tr className="bg-slate-900 text-white">
                    <td className="px-4 py-2.5 text-xs uppercase tracking-widest font-semibold" colSpan={2}>Total COGS</td>
                    <td className="px-4 py-2.5 text-right font-mono font-bold">{inr(reportData.total_cogs)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>
      )}

      <Dialog open={!!adjusting} onOpenChange={(v) => !v && setAdjusting(null)}>
        <DialogContent data-testid="adjust-dialog">
          <DialogHeader>
            <DialogTitle>Adjust stock — {adjusting?.name}</DialogTitle>
            <DialogDescription>
              Currently {num(adjusting?.stock_on_hand || 0, 3)} {adjusting?.unit_symbol} in total.
              Every adjustment is recorded in the movement ledger.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-3">
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label className="text-xs">Warehouse</Label>
                <Select value={adjustForm.warehouse_id}
                  onValueChange={(v) => setAdjustForm((f) => ({ ...f, warehouse_id: v }))}>
                  <SelectTrigger data-testid="adjust-warehouse"><SelectValue placeholder="Default" /></SelectTrigger>
                  <SelectContent>
                    {warehouses.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">How</Label>
                <Select value={adjustForm.mode} onValueChange={(v) => setAdjustForm((f) => ({ ...f, mode: v }))}>
                  <SelectTrigger data-testid="adjust-mode"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="CHANGE">Add or remove a quantity</SelectItem>
                    <SelectItem value="SET">Set the counted stock</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {adjustForm.mode === "CHANGE" ? (
              <div className="space-y-1.5">
                <Label className="text-xs">Change (negative to remove)</Label>
                <Input data-testid="adjust-quantity" inputMode="decimal" className="text-right font-mono"
                  value={adjustForm.quantity}
                  onChange={(e) => setAdjustForm((f) => ({ ...f, quantity: e.target.value }))}
                  placeholder="e.g. 10 or -3" />
              </div>
            ) : (
              <div className="space-y-1.5">
                <Label className="text-xs">Counted stock in this warehouse</Label>
                <Input data-testid="adjust-new-quantity" inputMode="decimal" className="text-right font-mono"
                  value={adjustForm.new_quantity}
                  onChange={(e) => setAdjustForm((f) => ({ ...f, new_quantity: e.target.value }))} />
              </div>
            )}
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label className="text-xs">Reason</Label>
                <Select value={adjustForm.movement_type}
                  onValueChange={(v) => setAdjustForm((f) => ({ ...f, movement_type: v }))}>
                  <SelectTrigger data-testid="adjust-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ADJUST_TYPES.map(([v, label]) => <SelectItem key={v} value={v}>{label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {adjusting?.track_batches && (
                <div className="space-y-1.5">
                  <Label className="text-xs">Batch</Label>
                  <Input data-testid="adjust-batch" className="font-mono" value={adjustForm.batch_number}
                    onChange={(e) => setAdjustForm((f) => ({ ...f, batch_number: e.target.value }))} />
                </div>
              )}
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Note</Label>
              <Textarea data-testid="adjust-reason" rows={2} value={adjustForm.reason}
                onChange={(e) => setAdjustForm((f) => ({ ...f, reason: e.target.value }))}
                placeholder="e.g. Stock count on 22 Aug, 3 units damaged" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAdjusting(null)}>Cancel</Button>
            <Button onClick={saveAdjust} data-testid="adjust-save" className="bg-blue-600 hover:bg-blue-700">
              Save adjustment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!movementsFor} onOpenChange={(v) => !v && setMovementsFor(null)}>
        <DialogContent className="max-w-3xl" data-testid="movements-dialog">
          <DialogHeader>
            <DialogTitle>{movementsFor?.name}</DialogTitle>
            <DialogDescription>Where the stock sits, and every movement behind it.</DialogDescription>
          </DialogHeader>

          {!!breakdown.length && (
            <div className="rounded-md border border-slate-200">
              <div className="border-b border-slate-100 px-3 py-2 text-xs font-semibold">Stock by location</div>
              <div className="divide-y divide-slate-100">
                {breakdown.map((b) => (
                  <div key={b.id} className="flex justify-between px-3 py-1.5 text-xs">
                    <span>
                      {b.warehouse_name}
                      {b.batch_number ? ` · batch ${b.batch_number}` : ""}
                      {b.expiry_date ? ` · exp ${fmtDate(b.expiry_date)}` : ""}
                    </span>
                    <span className="font-mono">{num(b.quantity, 3)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="movements-table">
              <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold">Date</th>
                  <th className="px-3 py-2 text-left font-semibold">Type</th>
                  <th className="px-3 py-2 text-left font-semibold">Warehouse</th>
                  <th className="px-3 py-2 text-left font-semibold">Reference</th>
                  <th className="px-3 py-2 text-right font-semibold">Change</th>
                  <th className="px-3 py-2 text-right font-semibold">Balance</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {movements.map((m) => (
                  <tr key={m.id}>
                    <td className="px-3 py-2 text-xs text-slate-600">{fmtDate(m.movement_date)}</td>
                    <td className="px-3 py-2 text-xs">{titleCase(m.movement_type)}</td>
                    <td className="px-3 py-2 text-xs text-slate-600">{m.warehouse_name || "—"}</td>
                    <td className="px-3 py-2 text-xs text-slate-600">{m.reference_label || m.notes || "—"}</td>
                    <td className={`px-3 py-2 text-right font-mono tabular-nums ${
                      m.quantity < 0 ? "text-rose-600" : "text-emerald-700"}`}>
                      {m.quantity > 0 ? "+" : ""}{num(m.quantity, 3)}
                    </td>
                    <td className="px-3 py-2 text-right font-mono tabular-nums">{num(m.balance_after, 3)}</td>
                  </tr>
                ))}
                {!movements.length && (
                  <tr><td colSpan={6} className="px-3 py-10 text-center text-sm text-slate-500">
                    No movements recorded yet
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
          <DialogFooter>
            <Button variant="outline" className="gap-1.5"
              onClick={() => exportCsv(`/inventory/export/movements.csv?item_id=${movementsFor.id}`,
                                       `movements-${movementsFor.name}.csv`)}>
              <Download size={14} /> Export CSV
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={whOpen} onOpenChange={setWhOpen}>
        <DialogContent data-testid="warehouse-dialog">
          <DialogHeader>
            <DialogTitle>{whEditing ? `Edit ${whEditing.name}` : "New warehouse"}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-3">
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label className="text-xs">Name</Label>
                <Input data-testid="warehouse-name" value={whForm.name}
                  onChange={(e) => setWhForm((f) => ({ ...f, name: e.target.value }))} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Code</Label>
                <Input data-testid="warehouse-code" className="font-mono" value={whForm.code || ""}
                  onChange={(e) => setWhForm((f) => ({ ...f, code: e.target.value }))} />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Address</Label>
              <Textarea rows={2} data-testid="warehouse-address" value={whForm.address || ""}
                onChange={(e) => setWhForm((f) => ({ ...f, address: e.target.value }))} />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <Switch checked={!!whForm.is_default} data-testid="warehouse-default"
                onCheckedChange={(v) => setWhForm((f) => ({ ...f, is_default: v }))} />
              Default warehouse for sales and receipts
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setWhOpen(false)}>Cancel</Button>
            <Button onClick={saveWarehouse} data-testid="warehouse-save" className="bg-blue-600 hover:bg-blue-700">
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={transferOpen} onOpenChange={setTransferOpen}>
        <DialogContent data-testid="transfer-dialog">
          <DialogHeader>
            <DialogTitle>Transfer stock</DialogTitle>
            <DialogDescription>
              Moves stock between warehouses. The total on hand and the average cost are unchanged.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Item</Label>
              <Select value={transfer.item_id} onValueChange={(v) => setTransfer((t) => ({ ...t, item_id: v }))}>
                <SelectTrigger data-testid="transfer-item"><SelectValue placeholder="Pick item" /></SelectTrigger>
                <SelectContent>
                  {rows.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label className="text-xs">From</Label>
                <Select value={transfer.from_warehouse_id}
                  onValueChange={(v) => setTransfer((t) => ({ ...t, from_warehouse_id: v }))}>
                  <SelectTrigger data-testid="transfer-from"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {warehouses.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">To</Label>
                <Select value={transfer.to_warehouse_id}
                  onValueChange={(v) => setTransfer((t) => ({ ...t, to_warehouse_id: v }))}>
                  <SelectTrigger data-testid="transfer-to"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {warehouses.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label className="text-xs">Quantity</Label>
                <Input className="text-right font-mono" inputMode="decimal" data-testid="transfer-qty"
                  value={transfer.quantity}
                  onChange={(e) => setTransfer((t) => ({ ...t, quantity: e.target.value }))} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Batch (optional)</Label>
                <Input className="font-mono" data-testid="transfer-batch" value={transfer.batch_number}
                  onChange={(e) => setTransfer((t) => ({ ...t, batch_number: e.target.value }))} />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Note</Label>
              <Input data-testid="transfer-notes" value={transfer.notes}
                onChange={(e) => setTransfer((t) => ({ ...t, notes: e.target.value }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTransferOpen(false)}>Cancel</Button>
            <Button onClick={saveTransfer} data-testid="transfer-save" className="bg-blue-600 hover:bg-blue-700">
              Transfer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
